/**
 * ============================================================
 * rolesSlice.ts — Grupos de Roles Customizados e Permissoes
 * ============================================================
 *
 * Responsabilidade: Gerenciar grupos de roles (papeis) customizados
 *   com permissoes granulares, e a atribuicao de membros a esses
 *   grupos. Permite criar roles como "Gestor de Trafego" ou
 *   "Gestor Comercial" com permissoes especificas.
 * Arquitetura: Slice do Zustand com persistencia em localStorage.
 *   Os roles customizados estendem os roles base (admin/editor/viewer)
 *   com permissoes especificas por funcionalidade.
 * Padroes: Zustand Slice, Persistencia localStorage, Roles defaults
 *   pre-configurados para onboarding rapido.
 * Dependencias:
 *   - Depende de: ../types, ../persistence
 *   - Quem depende dele: useStore, componentes de settings/team,
 *     hook useProjectRole (verifica permissoes do usuario)
 *
 * ============================================================
 */

import type { StateCreator } from 'zustand';
import type { CustomRoleGroup, PermissionKey } from '../types';
import { loadPersisted, persist } from '../persistence';

// ── Mapas de Permissoes Padrao ──

/** Todas as permissoes ativadas — usado como base para roles de admin */
const ALL_PERMISSIONS_TRUE: Record<PermissionKey, boolean> = {
  canManageLeads: true, canManageTasks: true, canManagePipeline: true,
  canViewDashboard: true, canViewAnalytics: true, canExportData: true,
  canManageTeam: true, canManageSettings: true, canManageAutomations: true,
};

/** Permissoes de editor — sem acesso a gestao de time, settings e automacoes */
const EDITOR_PERMISSIONS: Record<PermissionKey, boolean> = {
  canManageLeads: true, canManageTasks: true, canManagePipeline: true,
  canViewDashboard: true, canViewAnalytics: true, canExportData: true,
  canManageTeam: false, canManageSettings: false, canManageAutomations: false,
};

// ── Roles Customizados Pre-configurados ──
// Criados por padrao para onboarding rapido de agencias brasileiras
export const DEFAULT_CUSTOM_ROLE_GROUPS: CustomRoleGroup[] = [
  {
    id: 'gestor-trafego', name: 'Gestor de Tráfego', baseRole: 'editor',
    permissions: { ...EDITOR_PERMISSIONS, canManageLeads: true, canViewAnalytics: true, canExportData: true },
    color: '#3b82f6', createdAt: new Date().toISOString(),
  },
  {
    id: 'gestor-comercial', name: 'Gestor Comercial', baseRole: 'editor',
    permissions: { ...EDITOR_PERMISSIONS, canManagePipeline: true, canManageLeads: true },
    color: '#10b981', createdAt: new Date().toISOString(),
  },
  {
    id: 'product-owner', name: 'Product Owner', baseRole: 'admin',
    permissions: { ...ALL_PERMISSIONS_TRUE },
    color: '#8b5cf6', createdAt: new Date().toISOString(),
  },
];

// ── Interface do Slice ──

export interface RolesSlice {
  customRoleGroups: CustomRoleGroup[];
  memberRoleAssignments: Record<string, string>;
  addCustomRoleGroup: (role: Omit<CustomRoleGroup, 'id' | 'createdAt'>) => void;
  updateCustomRoleGroup: (id: string, updates: Partial<CustomRoleGroup>) => void;
  removeCustomRoleGroup: (id: string) => void;
  assignMemberRole: (memberId: string, roleGroupId: string | null) => void;
}

// ── Criacao do Slice ──
export const createRolesSlice: StateCreator<RolesSlice, [], [], RolesSlice> = (set) => ({
  // Estado inicial carregado do localStorage, com fallback para defaults
  customRoleGroups: loadPersisted<CustomRoleGroup[]>('bl-customRoleGroups', DEFAULT_CUSTOM_ROLE_GROUPS),
  // Mapa memberId -> roleGroupId (quem pertence a qual grupo)
  memberRoleAssignments: loadPersisted<Record<string, string>>('bl-memberRoleAssignments', {}),

  // ── CRUD de Grupos de Roles ──

  addCustomRoleGroup: (role) =>
    set((s) => {
      // Gera ID unico via crypto.randomUUID() (nativo do browser)
      const newGroup: CustomRoleGroup = { ...role, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
      const updated = [...s.customRoleGroups, newGroup];
      persist('bl-customRoleGroups', updated);
      return { customRoleGroups: updated };
    }),

  updateCustomRoleGroup: (id, updates) =>
    set((s) => {
      const updated = s.customRoleGroups.map((g) => (g.id === id ? { ...g, ...updates } : g));
      persist('bl-customRoleGroups', updated);
      return { customRoleGroups: updated };
    }),

  // Ao remover um grupo, tambem limpa as atribuicoes de membros que usavam esse grupo
  removeCustomRoleGroup: (id) =>
    set((s) => {
      const updated = s.customRoleGroups.filter((g) => g.id !== id);
      // Remove atribuicoes orfas (membros que pertenciam ao grupo deletado)
      const assignments = { ...s.memberRoleAssignments };
      Object.keys(assignments).forEach((k) => {
        if (assignments[k] === id) delete assignments[k];
      });
      persist('bl-customRoleGroups', updated);
      persist('bl-memberRoleAssignments', assignments);
      return { customRoleGroups: updated, memberRoleAssignments: assignments };
    }),

  // ── Atribuicao de Membro a Grupo ──
  // Se roleGroupId for null, remove a atribuicao (membro volta ao role base)
  assignMemberRole: (memberId, roleGroupId) =>
    set((s) => {
      const assignments = { ...s.memberRoleAssignments };
      if (roleGroupId) assignments[memberId] = roleGroupId;
      else delete assignments[memberId];
      persist('bl-memberRoleAssignments', assignments);
      return { memberRoleAssignments: assignments };
    }),
});
